import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import timeGridPlugin from '@fullcalendar/timegrid';
import timelinePlugin from '@fullcalendar/timeline';
import { Card } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { CalendarStyle, CalendarToolbar } from 'src/components/calendar';
import useResponsive from 'src/hooks/useResponsive';
import HttpServices from 'src/services/httpService';
import { postsModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';
import { CalendarUtil } from './calendarUtil';
import CalendarGridView from './post-view/calendar-grid-view.component';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { StyledComponent } from 'src/components/styled-components/styledComponent';
import { DemoHelper } from 'src/utils/demoUtil/demoUtil';


export default function Calendar() {
  const { t: translate } = useTranslation();

  const filterOption = {
    success: { Value: 'SUCCESS', Label: translate('calendar.success'), Color: '#4CAF50' },
    all: { Value: 'ALL', Label: translate('calendar.all'), Color: '#FFEEF4' },
    failed: { Value: 'FAILED', Label: translate('calendar.failed'), Color: '#F44336' },
    partialSuccess: { Value: 'PARTIAL_SUCCESS', Label: translate('calendar.partial_success'), Color: '#FFEEF4' },
    yetToDo: { Value: 'YET_TO_DO', Label: translate('calendar.yet_to_do'), Color: '#CAF4FF' },
    rejected: { Value: 'REJECTED', Label: translate('calendar.rejected'), Color: '#FFD0EC' },
    ellapsed: { Value: 'ELLAPSED', Label: translate('calendar.elapsed'), Color: '#9C27B0' },
    pending: { Value: 'PENDING', Label: translate('calendar.pending_approval'), Color: '#F8FDCF' },
  };

  const [date, setDate] = useState(new Date());
  const { showToast } = useToaster();
  const calendarRef = useRef(null);
  const isDesktop = useResponsive('up', 'sm');
  const [events, setEvents] = useState([]);
  const [view, setView] = useState(isDesktop ? CalendarUtil.CalenderType.DayMonth : CalendarUtil.CalenderType.ListWeek);
  const currentDate = new Date();
  const fromDateInitial = new Date(currentDate);
  fromDateInitial.setDate(fromDateInitial.getDate() - 30);
  const toDateInitial = new Date(currentDate);
  toDateInitial.setDate(toDateInitial.getDate() + 30);
  const [filter, setFilter] = useState(filterOption.all.Value);
  const [filteredEvents, setFilteredEvents] = useState(events);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isOrgEvent, setOrgEvent] = useState(CommonHelper.GetIsOrgLevelEvent());
  const hasOrgAdminPermission = CommonHelper.CheckForPermissionAvailableForUser(CommonHelper.PermissionList.ORG_ADMIN);

  useEffect(() => {
    getEvents(fromDateInitial, toDateInitial);
  }, [isOrgEvent]);

  useEffect(() => {
    filterEvents();
  }, [filter, events]);

  const eventsSuccessCallback = (data, message) => {
    let eventData = [];
    if (data && data.length > 0)
      eventData = data.map((a) => {
        const startTime = new Date(CommonHelper.GetBrandTimeFromUTC(a.scheduled_time));
        const endTime = new Date(startTime.getTime() + 600);
        return {
          id: a.id,
          userId: a.user_id,
          title: a.description,
          description: a.description,
          textColor: a.color,
          start: startTime,
          end: endTime,
          data: a,
        };
      });
    setEvents({
      events: eventData,
    });
  };
  const handleOrgConnectedProfiles = (event) => {
    const checked = event.target.checked;
    setOrgEvent(checked);
    CommonHelper.SetIsOrgLevelEvent(checked);
  };

  const getEvents = (fromDate = fromDateInitial, toDate = toDateInitial) => {
    let fDate = new Date(fromDate).toISOString();
    let tDate = new Date(toDate).toISOString();
    let postProps = {
      url: `${isOrgEvent ? postsModuleURL.getOrgCalendarEvents : postsModuleURL.getCalendarEvents}${fDate}&to=${tDate}`,
      successCallback: eventsSuccessCallback,
    };
    HttpServices.Get(postProps);
  };

  const filterEvents = () => {
    if (filter === filterOption.all.Value) {
      setFilteredEvents(events);
    } else {
      const filtered = events?.events?.filter((event) => event.data.status === filter);
      setFilteredEvents(filtered);
    }
  };

  const handlefilterEvents = (value) => {
    setFilter(value);
  };

  const handleClickToday = () => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.today();
      setDate(calendarApi.getDate());
    }
  };

  const handleChangeView = (newView) => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.changeView(newView);
      setView(newView);
    }
  };

  const scheduledEventSuccessCallback = (data, message) => {
    if (data && data.length > 0) loadPostData(data);
  };

  const scheduledEventFailureCallback = (message) => {
    showToast(message, 'e');
  };

  const handleSelectEvent = (arg) => {
    let postId = arg.event.id;
    let postProps = {
      url: isOrgEvent ? postsModuleURL.orgScheduledEventInfo + postId  : postsModuleURL.scheduledEventInfo + postId,
      successCallback: scheduledEventSuccessCallback,
      failureCallback: scheduledEventFailureCallback,
    };
    HttpServices.Get(postProps);
  };

  const loadPostData = (response) => {
    let images = [];
    let imageData = response[0].media_url || response[0].media_urls;
    if (imageData) {
      images = imageData.split(',');
    }
    let serviceNames = CommonHelper.GetUniqueValueFromArray(response.map((a) => a.service_name));
    let serviceIds = CommonHelper.GetUniqueValueFromArray(response.map((a) => a.service_id));
    let sEvent = {
      message: response[0].text,
      scheduled_time: new Date(CommonHelper.GetLocalTimefromUTC(response[0].created_time)),
      pageIds: response.map((a) => a.connected_profile_id),
      serviceNames: serviceNames,
      serviceIds: serviceIds,
      privacy: response[0].privacy,
      images: images,
      schedule_id: response[0].schedule_post_id,
    };
  };

  const handleClickDateNext = () => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.next();
      let fDate = calendarApi.getDate();
      let tDate = new Date(fDate);
      switch (view) {
        case CalendarUtil.CalenderType.DayMonth:
          tDate.setDate(tDate.getDate() + 31);
          fDate.setDate(fDate.getDate() - 31);
          break;
        case CalendarUtil.CalenderType.ListWeek:
          tDate.setDate(tDate.getDate() + 7);
          fDate.setDate(fDate.getDate() - 7);
          break;
        case CalendarUtil.CalenderType.TimeGridWeek:
          tDate.setDate(tDate.getDate() + 7);
          fDate.setDate(fDate.getDate() - 7);
          break;
        case CalendarUtil.CalenderType.TimeGridDay:
          tDate.setDate(tDate.getDate() + 1);
          fDate.setDate(fDate.getDate() - 1);
          break;
        default:
          break;
      }
      tDate.setHours(0, 0, 0, 0);
      setDate(calendarApi.getDate());
      getEvents(fDate, tDate);
    }
  };

  const handleClickDatePrev = () => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.prev();
      let fDate = calendarApi.getDate();
      let tDate = new Date(fDate);
      switch (view) {
        case CalendarUtil.CalenderType.DayMonth:
          tDate.setDate(tDate.getDate() + 31);
          fDate.setDate(fDate.getDate() - 31);
          break;
        case CalendarUtil.CalenderType.ListWeek:
          tDate.setDate(tDate.getDate() + 7);
          fDate.setDate(fDate.getDate() - 7);
          break;
        case CalendarUtil.CalenderType.TimeGridWeek:
          tDate.setDate(tDate.getDate() + 7);
          fDate.setDate(fDate.getDate() - 7);
          break;
        case CalendarUtil.CalenderType.TimeGridDay:
          tDate.setDate(tDate.getDate() + 1);
          fDate.setDate(fDate.getDate() - 1);
          break;
        default:
          break;
      }
      tDate.setHours(0, 0, 0, 0);
      setDate(calendarApi.getDate());
      getEvents(fDate, tDate);
    }
  };
  return (
    <>
      <Helmet>
        <title>{translate('navbar.menu.calender')}</title>
      </Helmet>
      {hasOrgAdminPermission && !DemoHelper.IsDemoAccount() &&(
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1, // add bottom margin for spacing
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: 'bold',
              color: 'text.primary', /* Lines 250-251 omitted */
            }}
          >
            {translate('common.enable_org_level_posts', 'Enable Org Level Posts')}
          </Typography>
          <StyledComponent.SwitchIcon size="small" checked={isOrgEvent} onChange={handleOrgConnectedProfiles} />
        </Box>
      )}
      <Card>
        <CalendarStyle>
          <CalendarToolbar
            date={CommonHelper.DateFormat(date)}
            view={view}
            filter={filter}
            filterOption={filterOption}
            onNextDate={handleClickDateNext}
            onPrevDate={handleClickDatePrev}
            onToday={handleClickToday}
            onChangeView={handleChangeView}
            handlefilterEvents={handlefilterEvents}
            isOrgEvent={isOrgEvent}
          />
          <FullCalendar
            weekends
            selectable
            events={filteredEvents}
            ref={calendarRef}
            rerenderDelay={1}
            initialDate={date}
            initialView={view}
            dayMaxEventRows={3}
            headerToolbar={false}
            eventClick={handleSelectEvent}
            height={isDesktop ? 'calc(94vh - 145px)' : 'calc(94vh - 210px)'}
            plugins={[listPlugin, dayGridPlugin, timelinePlugin, timeGridPlugin, interactionPlugin]}
            eventContent={({ event }) => {
              return (
                <CalendarGridView
                  view={view}
                  event={event}
                  handleRecallEvents={getEvents}
                  isOrgEvent={isOrgEvent}
                ></CalendarGridView>
              );
            }}
          />
        </CalendarStyle>
      </Card>
    </>
  );
}
